import React, { useState, useEffect, useCallback } from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import moment from "moment";

import { ProgressSpinner } from "primereact/progressspinner";

// Load Actions
import * as OrderActions from "../Store/Actions/Order";

// 30 minutos em milissegundos — janela após a qual recarregamos o câmbio
// quando o usuário retorna à aba.
const STALE_THRESHOLD_MS = 30 * 60 * 1000;

/**
 * CurrencyBox — Exibe o câmbio USD/BRL no header.
 *
 * Regras:
 * - Renderiza apenas quando o usuário está logado (loginStatus=true).
 * - No mount, busca a cotação atual (sempre fresh — não confia em estado herdado).
 * - Escuta `visibilitychange`: quando a aba volta a ser visível, se o último
 *   carregamento bem-sucedido foi há mais de 30min, refaz a busca.
 * - Despacha o array de cotações ao reducer global (orderReducer.exchangeRates),
 *   compartilhando com outras telas (hotelBooking, hotelDetails, etc.).
 * - Em caso de erro, oculta o componente silenciosamente (sem alarme no header).
 * - onClick: handler vazio — owner definirá ação futura.
 */
const CurrencyBox = (props) => {
	const { loginStatus, exchangeRates, retrieveExchangeRate, setExchangeRates } = props;

	const [loading, setLoading] = useState(false);
	const [loadedAt, setLoadedAt] = useState(null);
	const [hasError, setHasError] = useState(false);

	const fetchAndSet = useCallback(async () => {
		setLoading(true);
		try {
			const result = await retrieveExchangeRate();
			if (result && result.success && result.exchangeRates) {
				setExchangeRates(result.exchangeRates);
				setLoadedAt(Date.now());
				setHasError(false);
			} else {
				setHasError(true);
			}
		} catch (err) {
			setHasError(true);
		} finally {
			setLoading(false);
		}
	}, [retrieveExchangeRate, setExchangeRates]);

	// Mount / login change — busca inicial quando logado.
	useEffect(() => {
		if (loginStatus) {
			fetchAndSet();
		} else {
			// reset ao deslogar
			setLoadedAt(null);
			setHasError(false);
			setLoading(false);
		}
	}, [loginStatus, fetchAndSet]);

	// Page Visibility API — refresh ao voltar à aba após 30min.
	useEffect(() => {
		if (!loginStatus) return undefined;

		const handleVisibilityChange = () => {
			if (document.visibilityState === "visible" && loadedAt) {
				const elapsed = Date.now() - loadedAt;
				if (elapsed > STALE_THRESHOLD_MS) {
					fetchAndSet();
				}
			}
		};

		document.addEventListener("visibilitychange", handleVisibilityChange);
		return () => {
			document.removeEventListener("visibilitychange", handleVisibilityChange);
		};
	}, [loginStatus, loadedAt, fetchAndSet]);

	// ------------------------------------------------------------------
	// Render
	// ------------------------------------------------------------------

	if (!loginStatus) return null;
	if (hasError) return null;

	const usd =
		Array.isArray(exchangeRates) && exchangeRates.length > 0
			? exchangeRates.find((item) => item.targetCurrencyInitials === "USD")
			: null;

	// Sem dados e sem loading ativo → não renderiza nada (graceful degrade).
	if (!usd && !loading) return null;

	// TODO(owner): definir ação do onClick (hoje placeholder).
	const handleClick = () => {};

	return (
		<button
			type="button"
			aria-label="Câmbio"
			onClick={handleClick}
			className="p-button p-component pT10 headerButton p-button-text cambio-box"
			style={{ fontSize: "0.75rem", border: "1px solid #e3e3e3", marginLeft: "2rem" }}
		>
			<span className="p-button-label p-c">
				{loading && !usd ? (
					<ProgressSpinner
						style={{ width: "18px", height: "18px" }}
						strokeWidth="4"
						animationDuration=".7s"
					/>
				) : (
					<div className="currency-info">
						<div
							className="currency-main"
							style={{ display: "flex", gap: "0.5rem" }}
						>
							<span className="currency-pair">USD/BRL</span>
							<span
								className="currency-rate"
								style={{ fontWeight: "bolder", color: "#814DE5" }}
							>
								{usd.valueFormatted}
							</span>
						</div>
						<span className="currency-date">
							{moment(usd.dateExpiration).subtract(1, "hour").format("DD/MM/YYYY HH:mm")}
						</span>
					</div>
				)}
			</span>
		</button>
	);
};

const mapStateToProps = (store) => ({
	loginStatus: store.appReducer.loginStatus,
	exchangeRates: store.orderReducer.exchangeRates,
});

const mapDispatchToProps = (dispatch) =>
	bindActionCreators({ ...OrderActions }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(CurrencyBox);
